import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Briefcase, Users, CalendarClock, MessageCircle, Plus } from "lucide-react";

export default function EmployerDashboard() {
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [interviews, setInterviews] = useState([]);

  useEffect(() => {
    api.get("/employers/me").then(r => setProfile(r.data));
    api.get("/jobs/mine").then(r => setJobs(r.data.items || []));
    api.get("/applications").then(r => setApps(r.data.items || []));
    api.get("/interviews").then(r => setInterviews(r.data.items || []));
  }, []);

  const openJobs = jobs.filter(j => j.is_active && j.is_approved).length;
  const newCandidates = apps.filter(a => a.status === "Submitted to Employer").length;
  const upcomingInts = interviews.filter(i => i.status === "Scheduled").length;
  const offers = apps.filter(a => a.status === "Offer Received").length;
  const hired = apps.filter(a => a.status === "Hired").length;

  return (
    <div data-testid={TID.employerDashboard}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Employer Portal</div>
          <h1 className="font-display text-3xl font-bold text-navy">{profile?.company_name || "Welcome"}</h1>
          <p className="text-slate-500 text-sm mt-1">Your open positions, submitted candidates, and interviews.</p>
        </div>
        <Link to="/dashboard/employer/jobs" data-testid={TID.employerCreateJob} className="btn-navy">
          <Plus className="w-4 h-4" /> Post a job
        </Link>
      </div>

      {profile && !profile.is_approved && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Your account is pending approval by our team. You can still complete your profile and post jobs — they'll go live after approval.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Briefcase, label: "Open positions", val: openJobs },
          { icon: Users, label: "New candidates", val: newCandidates },
          { icon: CalendarClock, label: "Upcoming interviews", val: upcomingInts },
          { icon: MessageCircle, label: "Offers pending", val: offers },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="card-lift border border-slate-200 rounded-xl p-5 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{k.label}</div>
                  <div className="mt-2 font-display text-3xl font-bold text-navy">{k.val}</div>
                </div>
                <div className="h-10 w-10 rounded-lg bg-gold/10 text-gold grid place-items-center"><Icon className="w-5 h-5" /></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-3">Recent candidate submissions</div>
          <div className="divide-y">
            {apps.slice(0, 6).map(a => (
              <div key={a.id} className="py-3 text-sm flex justify-between items-center">
                <div>
                  <div className="font-medium text-navy">{a.candidate_name}</div>
                  <div className="text-xs text-slate-500">{a.job_title}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-slate-100">{a.status}</span>
              </div>
            ))}
            {apps.length === 0 && <div className="text-sm text-slate-500 py-4">No submissions yet — your recruiter will submit vetted candidates here.</div>}
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-3">Recent hires</div>
          <div className="divide-y">
            {apps.filter(a => a.status === "Hired").map(a => (
              <div key={a.id} className="py-3 text-sm">
                <div className="font-medium text-navy">{a.candidate_name}</div>
                <div className="text-xs text-slate-500">{a.job_title} • {new Date(a.updated_at).toLocaleDateString()}</div>
              </div>
            ))}
            {apps.filter(a => a.status === "Hired").length === 0 && <div className="text-sm text-slate-500 py-4">No hires yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
